import { useState, useEffect, useRef, MouseEvent as ReactMouseEvent } from 'react';
import styled from 'styled-components';
import { Accordion, Spinner } from 'react-bootstrap';
import { Image, ImageType } from 'redux/annotations/annotations.type';
import { ButtonThumbnail } from 'components/shared/image-thumbnail/image-thumbnail';
import { useRedux, useReduxDispatch, useReduxSelector } from 'redux/store.hooks';

const Thumbnails = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

const ImageThumbnailWrapper = styled.div<{ $active: boolean }>`
  margin-top: 0.5rem;
  margin-right: 0.75rem;
  padding: 2px;
  border: 2px solid ${({ $active }) => ($active ? '#0d6efd' : 'transparent')};
  border-radius: 4px;
  box-shadow: ${({ $active }) => ($active ? '0 0 0 1px #0d6efd' : 'none')};
  transition: border-color 0.15s, box-shadow 0.15s;
`;

const Preview = styled.div`
  margin-top: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
`;

const PreviewTitle = styled.div`
  margin-top: 1rem;
  font-size: 0.875rem;
  text-align: center;
`;

const ImageContainer = styled.div<{ $zoomed: boolean, $dragging: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 500px;
  overflow: hidden;
  position: relative;
  user-select: none;
  cursor: ${({ $zoomed, $dragging }) => {
    if ($dragging) return 'grabbing';
    return $zoomed ? 'grab' : 'default';
  }};
`;

const FullImage = styled.img`
  max-width: 100%;
  max-height: 85vh;
  object-fit: contain;
  transform-origin: center center;
  will-change: transform;
`;

const ZoomHint = styled.div`
  margin-top: 0.25rem;
  text-align: center;
  font-size: 0.75rem;
  color: #6c757d;
`;

const NavButton = styled.button`
  background: rgba(0, 0, 0, 0.35);
  border: none;
  border-radius: 50%;
  width: 2.5rem;
  height: 2.5rem;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  cursor: pointer;
  transition: background 0.15s;

  &:hover:not(:disabled) {
    background: rgba(0, 0, 0, 0.6);
  }

  &:disabled {
    opacity: 0.2;
    cursor: default;
  }
`;

// Same chevron icons used for Previous/Next in the labelling footer (image-detail.footer).
const chevronSvgProps = {
  width: 16,
  height: 16,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
};

const ArrowLeft = () => (
  <svg {...chevronSvgProps}><polyline points="15 18 9 12 15 6" /></svg>
);

const ArrowRight = () => (
  <svg {...chevronSvgProps}><polyline points="9 18 15 12 9 6" /></svg>
);

interface AdditionalImagesProps {
  additionalImages: Image[] | undefined,
}

const AdditionalImages = (props: AdditionalImagesProps) => {
  const { additionalImages } = props;

  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  const viewportRef = useRef<HTMLDivElement>(null);
  const [view, setView] = useState({ scale: 1, x: 0, y: 0 });
  const viewRef = useRef(view);
  viewRef.current = view;
  const dragStartRef = useRef<{ x: number, y: number } | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const { images: { actions: imagesActions, selectors: imagesSelectors } } = useRedux();
  const dispatch = useReduxDispatch();

  const selectedImageId = selectedIndex !== null
    ? additionalImages?.[selectedIndex]?.id
    : undefined;

  const imageFile = useReduxSelector((state) =>
    selectedImageId ? imagesSelectors.selectImageFile(state, selectedImageId) : undefined);

  useEffect(() => {
    if (selectedImageId && !imageFile) {
      dispatch(imagesActions.downloadImageFile({ imageId: selectedImageId }));
    }
  }, [selectedImageId, imageFile, dispatch, imagesActions]);

  // Reset zoom/pan when the shown image changes.
  useEffect(() => {
    setView({ scale: 1, x: 0, y: 0 });
  }, [selectedImageId]);

  // Ctrl + wheel to zoom towards the cursor. Native listener so we can
  // preventDefault (React's onWheel is passive and can't block page zoom).
  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return undefined;

    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey) return;
      e.preventDefault();

      const rect = el.getBoundingClientRect();
      const cx = e.clientX - rect.left - rect.width / 2;
      const cy = e.clientY - rect.top - rect.height / 2;

      const prev = viewRef.current;
      const scale = Math.min(8, Math.max(1, prev.scale * (e.deltaY > 0 ? 1.15 : 1 / 1.15)));

      if (scale === 1) {
        setView({ scale: 1, x: 0, y: 0 });
        return;
      }

      const k = scale / prev.scale;
      setView({ scale, x: cx - k * (cx - prev.x), y: cy - k * (cy - prev.y) });
    };

    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [selectedImageId]);

  // Drag to pan while zoomed in.
  useEffect(() => {
    if (!isDragging) return undefined;

    const onMove = (e: MouseEvent) => {
      const start = dragStartRef.current;
      if (!start) return;
      const dx = e.clientX - start.x;
      const dy = e.clientY - start.y;
      dragStartRef.current = { x: e.clientX, y: e.clientY };
      setView((v) => ({ ...v, x: v.x + dx, y: v.y + dy }));
    };
    const onUp = () => {
      dragStartRef.current = null;
      setIsDragging(false);
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [isDragging]);

  const handleImageMouseDown = (e: ReactMouseEvent) => {
    if (viewRef.current.scale === 1) return;
    e.preventDefault();
    dragStartRef.current = { x: e.clientX, y: e.clientY };
    setIsDragging(true);
  };

  if (!additionalImages || additionalImages.length === 0) return null;

  const handleThumbnailClick = (id: string) => {
    const idx = additionalImages.findIndex((img) => img.id === id);
    setSelectedIndex(idx);
  };

  const handlePrev = () => setSelectedIndex((i) => Math.max(0, (i ?? 0) - 1));
  const handleNext = () =>
    setSelectedIndex((i) => Math.min(additionalImages.length - 1, (i ?? 0) + 1));

  const selectedImage = selectedIndex !== null ? additionalImages[selectedIndex] : null;

  return (
    <Accordion.Item eventKey="additional-images">
      <Accordion.Header>Additional images</Accordion.Header>
      <Accordion.Body>
        <Thumbnails>
          {additionalImages.map((image: Image, index: number) => (
            <ImageThumbnailWrapper key={image.id} $active={index === selectedIndex}>
              <ButtonThumbnail
                filePath={image.filePath}
                fileName={image.fileName}
                width="small"
                id={image.id}
                onClick={handleThumbnailClick}
              />
            </ImageThumbnailWrapper>
          ))}
        </Thumbnails>

        {selectedImage && (
          <>
            <PreviewTitle>
              {ImageType[selectedImage.imageType]}
              {additionalImages.length > 1 && (
                <span className="text-muted ms-2">
                  {(selectedIndex ?? 0) + 1} / {additionalImages.length}
                </span>
              )}
            </PreviewTitle>
            <Preview>
              <NavButton onClick={handlePrev} disabled={selectedIndex === 0}>
                <ArrowLeft />
              </NavButton>
              <ImageContainer
                ref={viewportRef}
                $zoomed={view.scale > 1}
                $dragging={isDragging}
                onMouseDown={handleImageMouseDown}
              >
                {imageFile
                  ? (
                    <FullImage
                      src={imageFile.localUrl}
                      alt={selectedImage.fileName}
                      draggable={false}
                      style={{
                        transform: `translate(${view.x}px, ${view.y}px) scale(${view.scale})`,
                      }}
                    />
                  )
                  : <Spinner animation="border" variant="secondary" />}
              </ImageContainer>
              <NavButton
                onClick={handleNext}
                disabled={selectedIndex === additionalImages.length - 1}
              >
                <ArrowRight />
              </NavButton>
            </Preview>
            <ZoomHint>Ctrl + scroll to zoom · drag to move</ZoomHint>
          </>
        )}
      </Accordion.Body>
    </Accordion.Item>
  );
};

export default AdditionalImages;
